import React, { useCallback, useRef, useState } from 'react';

import { GlossaryRow, UploadWarning } from '../constants';
import { SUPPORTED_LANGUAGE_CODES } from '../constants/glossaryLanguages';
import { parseCsv } from '../utils/csvParser';
import { decodeBuffer } from '../utils/decodeBuffer';

/**
 * CSV ファイルのアップロード・パースに関する状態とハンドラーを管理するカスタムフック。
 */
export const useFileUpload = () => {
  /** ファイル選択 input への ref（ボタンからのファイル選択ダイアログ起動用） */
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** 並行読み込みの競合を防ぐための読み込み ID。ファイル選択ごとにインクリメントする。 */
  const readIdRef = useRef(0);
  /** 進行中の FileReader。中断（abort）のために保持する。 */
  const readerRef = useRef<FileReader | null>(null);

  /** 選択中のファイル。null のときはドロップゾーンを表示する。 */
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  /** パース済みエントリ。登録ボタン押下時に localStorage へ書き込む。 */
  const [pendingEntries, setPendingEntries] = useState<GlossaryRow[]>([]);
  /** ヘッダー除外後のデータ行数。0 のとき空CSV（全削除）。null のときは未解析。 */
  const [pendingDataRowCount, setPendingDataRowCount] = useState<number | null>(
    null,
  );
  /** CSV パース時に検出された警告一覧 */
  const [uploadWarnings, setUploadWarnings] = useState<UploadWarning[]>([]);
  /** ファイル読み込み中かどうか（スピナー表示・二重操作防止用） */
  const [isReading, setIsReading] = useState(false);
  /** ドラッグオーバー中かどうか（ドロップゾーンのスタイル切替用） */
  const [isDragging, setIsDragging] = useState(false);
  /** CSV 以外のファイルが選択されたときのエラーフラグ */
  const [fileTypeError, setFileTypeError] = useState(false);
  /** FileReader による読み込み失敗フラグ */
  const [readError, setReadError] = useState(false);
  /** デコード・パース失敗フラグ */
  const [parseError, setParseError] = useState(false);
  /** 1行目がヘッダー行でないときのエラーフラグ */
  const [headerError, setHeaderError] = useState(false);

  /**
   * 進行中の読み込みを中断し、パース結果とエラー状態をすべて初期化する。
   * readIdRef を進めることで進行中の読み込み結果を破棄し、ファイルの復活を防ぐ。
   */
  const resetPreview = useCallback(() => {
    readIdRef.current += 1;
    readerRef.current?.abort();
    readerRef.current = null;

    setPendingFile(null);
    setPendingEntries([]);
    setPendingDataRowCount(null);
    setUploadWarnings([]);
    setIsReading(false);
    setFileTypeError(false);
    setReadError(false);
    setParseError(false);
    setHeaderError(false);
  }, []);

  /**
   * ファイルを受け取り、拡張子チェック → FileReader で読み込み → パースを行う。
   * ArrayBuffer として読み込んで文字コードを自動判別する。
   *
   * 冒頭で必ず resetPreview() を呼ぶことで、
   * - 拡張子エラー・読み込みエラー時に前のファイルの状態が残らない
   * - 進行中の読み込み結果が後から着地しない
   * ことを構造的に保証する。
   */
  const handleFileSelect = useCallback(
    (file: File) => {
      resetPreview();

      if (!file.name.toLowerCase().endsWith('.csv')) {
        setFileTypeError(true);

        return;
      }

      const readId = readIdRef.current;
      const reader = new FileReader();

      readerRef.current = reader;
      setIsReading(true);

      reader.onerror = () => {
        if (readId !== readIdRef.current) return;
        setIsReading(false);
        setReadError(true);
      };

      reader.onload = (event) => {
        if (readId !== readIdRef.current) return;
        setIsReading(false);

        const buffer = event.target?.result;

        if (!(buffer instanceof ArrayBuffer)) {
          setReadError(true);

          return;
        }

        // decodeBuffer / parseCsv の例外はここで捕捉する。
        // onload はイベントハンドラなので、throw すると uncaught error になり
        // state が一切更新されず UI が読み込み中のまま止まってしまう。
        try {
          const {
            entries: previewEntries,
            warnings,
            dataRowCount,
            headerError: hasHeaderError,
          } = parseCsv(decodeBuffer(buffer), SUPPORTED_LANGUAGE_CODES);

          // ヘッダー不一致はファイル自体を受け付けない。
          // pendingFile を立てないことでドロップゾーンのまま
          // エラーメッセージを表示し、別のファイルを選び直させる。
          if (hasHeaderError) {
            setHeaderError(true);

            return;
          }

          setPendingFile(file);
          setPendingEntries(previewEntries);
          setPendingDataRowCount(dataRowCount);
          setUploadWarnings(warnings);
        } catch {
          setParseError(true);
        }
      };

      reader.readAsArrayBuffer(file);
    },
    [resetPreview],
  );

  /**
   * ドロップ時にファイルを取り出して handleFileSelect に渡す。
   */
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];

      if (file) handleFileSelect(file);
    },
    [handleFileSelect],
  );

  /**
   * ドラッグオーバー中にデフォルト動作を抑止し、ドロップゾーンをアクティブにする。
   */
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  }, []);

  /**
   * ドラッグがドロップゾーン外に出たときにアクティブ状態を解除する。
   * ドロップゾーン内の子要素をまたぐ際にも dragleave が発火するため、
   * relatedTarget が内側にある場合は無視してハイライトのちらつきを防ぐ。
   */
  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsDragging(false);
  }, []);

  /**
   * ファイル選択 input の onChange。
   * 選択後に input をリセットして同一ファイルの再選択を可能にする。
   */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];

      if (file) handleFileSelect(file);

      e.target.value = '';
    },
    [handleFileSelect],
  );

  /** インポートされる件数（有効行数）。null のときはまだ解析前。 */
  const importCount =
    pendingDataRowCount === null ? null : pendingEntries.length;

  /**
   * データ行が存在するにもかかわらず全行除外された状態。
   * UI でインポート不可メッセージに切り替える。
   */
  const isAllExcluded =
    pendingDataRowCount !== null &&
    pendingDataRowCount > 0 &&
    pendingEntries.length === 0;

  /**
   * 登録ボタンを有効にしてよいか。
   * - 未解析（dataRowCount が null）→ false
   * - 読み込み中 → false
   * - いずれかのエラー中 → false
   * - isAllExcluded（全行除外）→ false
   * - それ以外（空CSV / 通常登録）→ true
   */
  const canRegister =
    pendingFile !== null &&
    pendingDataRowCount !== null &&
    !isAllExcluded &&
    !isReading &&
    !fileTypeError &&
    !readError &&
    !parseError &&
    !headerError;

  return {
    pendingFile,
    pendingEntries,
    importCount,
    pendingDataRowCount,
    canRegister,
    isAllExcluded,
    uploadWarnings,
    isReading,
    isDragging,
    fileTypeError,
    readError,
    parseError,
    headerError,
    fileInputRef,
    handleRemoveFile: resetPreview,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleInputChange,
  };
};
